import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plane, LogOut, Inbox, PlaneTakeoff, MessageSquare, BadgeAlert,
  Trash2, ChevronDown, ChevronRight, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  new: { label: "Nou", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "În lucru", cls: "bg-sky-100 text-sky-800" },
  won: { label: "Câștigat", cls: "bg-green-100 text-green-800" },
  closed: { label: "Închis", cls: "bg-stone-200 text-stone-700" },
};

const LABELS = {
  full_name: "Nume", email: "Email", phone: "Telefon", flight_number: "Nr. zbor",
  flight_date: "Data", departure_airport: "Plecare", arrival_airport: "Sosire",
  airline: "Companie", issue_type: "Problemă", delay_length: "Întârziere",
  details: "Detalii", message: "Mesaj",
  consent_accuracy: "Info corecte", consent_contact: "Acord contact",
  consent_no_guarantee: "Acceptă fără garanție", consent_terms: "Acceptă T&C",
  estimate_distance: "Distanță (estimare)", estimate_gross: "Compensație estimată (£)",
  estimate_net: "Net estimat (£)",
};

const fmt = (v) => (typeof v === "boolean" ? (v ? "Da" : "Nu") : v);

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className={`grid place-items-center h-10 w-10 rounded-xl ${color}`}><Icon className="h-5 w-5" /></span>
        <span className="font-head font-extrabold text-3xl text-stone-900">{value}</span>
      </div>
      <div className="mt-3 text-sm text-stone-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, eligibility: 0, contact: 0, new: 0 });
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const kind = filter === "all" ? "" : `?kind=${filter}`;
      const [s, st] = await Promise.all([
        axios.get(`${API}/admin/submissions${kind}`),
        axios.get(`${API}/admin/stats`),
      ]);
      setSubs(s.data);
      setStats(st.data);
    } catch (e) { if (e.response?.status === 401) navigate("/login"); }
  }, [filter, navigate]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/submissions/${id}`, { status });
    toast.success("Status actualizat");
    load();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/submissions/${id}`);
    toast.success("Șters");
    load();
  };

  if (!user) return <div className="min-h-screen grid place-items-center bg-stone-100 text-stone-500">Se încarcă...</div>;

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-dashboard">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-sky-700 text-white"><Plane className="h-5 w-5 -rotate-45" /></span>
            <span className="font-head font-extrabold text-lg text-stone-900">ZborHelp <span className="text-stone-400 font-medium">/ Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-stone-500 hover:text-sky-700 hidden sm:inline-flex items-center gap-1" data-testid="view-site">
              <ExternalLink className="h-4 w-4" /> Site
            </Link>
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className="rounded-full">
              <LogOut className="h-4 w-4 mr-2" /> Ieșire
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Inbox} label="Total cereri" value={stats.total} color="bg-stone-100 text-stone-700" />
          <StatCard icon={PlaneTakeoff} label="Verificări zbor" value={stats.eligibility} color="bg-sky-100 text-sky-700" />
          <StatCard icon={MessageSquare} label="Mesaje contact" value={stats.contact} color="bg-amber-100 text-amber-700" />
          <StatCard icon={BadgeAlert} label="Noi" value={stats.new} color="bg-green-100 text-green-700" />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h1 className="font-head font-bold text-2xl text-stone-900">Cereri</h1>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="filter-tabs">
              <TabsTrigger value="all" data-testid="filter-all">Toate</TabsTrigger>
              <TabsTrigger value="eligibility" data-testid="filter-eligibility">Zbor</TabsTrigger>
              <TabsTrigger value="contact" data-testid="filter-contact">Contact</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-5 space-y-3">
          {subs.length === 0 && <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-400" data-testid="empty-state">Nicio cerere încă.</div>}
          {subs.map((s) => {
            const open = expanded === s.id;
            const st = STATUS[s.status] || STATUS.new;
            return (
              <div key={s.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`submission-${s.id}`}>
                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-stone-50" onClick={() => setExpanded(open ? null : s.id)}>
                  {open ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
                  <Badge className={`${s.kind === "eligibility" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"} hover:bg-current/10`}>
                    {s.kind === "eligibility" ? "Zbor" : "Contact"}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900 truncate">{s.data.full_name}</div>
                    <div className="text-xs text-stone-500 truncate">{s.data.email}</div>
                  </div>
                  {s.kind === "eligibility" && s.data.estimate_net != null && (
                    <span className="text-sm font-bold text-sky-700 hidden sm:block whitespace-nowrap" data-testid={`estimate-${s.id}`} title="Net estimat">
                      ~£{s.data.estimate_net}
                    </span>
                  )}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                  <span className="text-xs text-stone-400 hidden sm:block">{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
                {open && (
                  <div className="border-t border-stone-100 p-5 bg-stone-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                      {Object.entries(s.data).map(([k, v]) => (v !== "" && v !== null && v !== undefined) ? (
                        <div key={k} className="flex gap-2 text-sm py-1 border-b border-stone-100">
                          <span className="text-stone-500 font-medium min-w-[110px]">{LABELS[k] || k}</span>
                          <span className="text-stone-900 break-words">{fmt(v)}</span>
                        </div>
                      ) : null)}
                    </div>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
                        <SelectTrigger className="w-44 h-10 bg-white" data-testid={`status-select-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <a href={`mailto:${s.data.email}`} className="text-sm text-sky-700 hover:underline">Răspunde pe email</a>
                      {user?.role === "admin" && (
  <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`delete-${s.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto">
    <Trash2 className="h-4 w-4 mr-1" /> Șterge
  </Button>
)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
